import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

export default function Storage() {
  const features = [
    {
      icon: 'ri-shield-check-line',
      title: 'Dedicated Storage Tanks',
      description: 'Each product type is assigned to specific dedicated fuel storage tanks, ensuring product integrity and preventing contamination.',
    },
    {
      icon: 'ri-git-branch-line',
      title: 'Independent Pipelines',
      description: 'Segregated pipeline systems for each product type eliminate any risk of cross-contamination during transfer operations.',
    },
    {
      icon: 'ri-eye-line',
      title: 'Real-Time Monitoring',
      description: 'Advanced monitoring systems provide continuous oversight of storage conditions, inventory levels, and product quality.',
    },
    {
      icon: 'ri-truck-line',
      title: 'On-Demand Dispatch',
      description: 'Efficient dispatch capabilities enable quick and reliable delivery of your petroleum products when you need them.',
    },
    {
      icon: 'ri-temperature-line',
      title: 'Climate Control',
      description: 'Sophisticated temperature and pressure control systems maintain optimal storage conditions for all product types.',
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Inventory Management',
      description: 'Comprehensive tracking and reporting systems provide complete visibility of your stored inventory.',
    },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section 
          className="relative min-h-[500px] flex items-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 20, 60, 0.7), rgba(0, 20, 60, 0.7)), url('https://public.readdy.ai/ai/img_res/cb7443f382dfeafdf43b7a0823f01cef.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">Storage of Petroleum Products</h1>
            <p className="text-xl text-gray-200 max-w-3xl leading-relaxed">
              Secure and specialized storage solutions with dedicated fuel storage tanks and independent pipelines to eliminate cross-contamination
            </p>
          </div>
        </section>

        {/* Overview Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="relative rounded-xl overflow-hidden shadow-lg">
                <img 
                  src="https://readdy.ai/api/search-image?query=Real%20industrial%20petroleum%20storage%20tanks%20with%20white%20cylindrical%20design%20at%20working%20tank%20farm%20facility%20showing%20modern%20infrastructure%20and%20operational%20safety%20systems%20under%20natural%20daylight%2C%20authentic%20documentary%20photography%20of%20actual%20oil%20storage%20terminal%20with%20multiple%20large%20tanks%20in%20organized%20rows%2C%20professional%20photojournalism%20of%20functioning%20petroleum%20terminal%20showing%20genuine%20industrial%20operations%20and%20professional%20standards%2C%20realistic%20commercial%20photograph&width=600&height=400&seq=storage-facility-001&orientation=landscape"
                  alt="Storage Facility"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="relative rounded-xl overflow-hidden shadow-lg">
                <img 
                  src="https://readdy.ai/api/search-image?query=Real%20petroleum%20storage%20tank%20farm%20aerial%20view%20showing%20organized%20layout%20of%20large%20white%20cylindrical%20tanks%20at%20working%20facility%2C%20authentic%20documentary%20photography%20of%20actual%20oil%20terminal%20infrastructure%20with%20modern%20monitoring%20systems%20and%20safety%20equipment%2C%20professional%20photojournalism%20of%20genuine%20industrial%20petroleum%20storage%20operations%20under%20clear%20sky%2C%20realistic%20commercial%20photograph%20of%20operational%20tank%20farm%20with%20professional%20industrial%20standards&width=600&height=400&seq=storage-facility-002&orientation=landscape"
                  alt="Tank Farm Overview"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
              <div className="relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=Real%20large%20white%20petroleum%20storage%20tanks%20at%20working%20industrial%20facility%2C%20authentic%20documentary%20photography%20of%20actual%20oil%20storage%20terminal%20with%20modern%20safety%20systems%20and%20monitoring%20equipment%2C%20professional%20photojournalism%20showing%20genuine%20tank%20farm%20operations%20with%20real%20infrastructure%20under%20natural%20lighting%2C%20realistic%20commercial%20photograph%20of%20operational%20petroleum%20storage%20facility&width=600&height=400&seq=storage-facility-001&orientation=landscape"
                  alt="Storage Facility"
                  className="rounded-2xl shadow-2xl w-full h-auto object-cover object-top"
                />
              </div>
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">Professional Storage Solutions</h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  We provide secure and specialized storage solutions for petroleum products, ensuring the integrity and quality of your goods are maintained throughout the storage period. Our state-of-the-art facility is designed with safety, efficiency, and product quality as top priorities.
                </p>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Our facility features a diverse fleet of dedicated fuel storage tanks, each assigned to specific product types. This segregated system, supported by independent pipelines, eliminates the risk of cross-contamination and ensures that each product maintains its original specifications.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Our methodical approach guarantees product quality and enables the efficient, on-demand dispatch of your inventory. With advanced monitoring systems and experienced personnel, we provide complete peace of mind for your petroleum storage needs.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Storage Features & Benefits</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Advanced infrastructure and systems designed for optimal petroleum product storage
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-shadow">
                  <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                    <i className={`${feature.icon} text-2xl text-blue-600`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Technical Specifications */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Technical Specifications</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Storage Infrastructure</h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-blue-600 text-xl mt-1"></i>
                    <div>
                      <strong className="text-gray-900">Dedicated Tank Fleet:</strong>
                      <span className="text-gray-600"> Multiple tanks assigned to specific product types</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-blue-600 text-xl mt-1"></i>
                    <div>
                      <strong className="text-gray-900">Independent Pipelines:</strong>
                      <span className="text-gray-600"> Segregated transfer systems for each product</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-blue-600 text-xl mt-1"></i>
                    <div>
                      <strong className="text-gray-900">Safety Systems:</strong>
                      <span className="text-gray-600"> Advanced fire suppression and leak detection</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-blue-600 text-xl mt-1"></i>
                    <div>
                      <strong className="text-gray-900">Access Control:</strong>
                      <span className="text-gray-600"> 24/7 security and restricted access protocols</span>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Monitoring & Control</h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-blue-600 text-xl mt-1"></i>
                    <div>
                      <strong className="text-gray-900">Real-Time Monitoring:</strong>
                      <span className="text-gray-600"> Continuous tracking of all storage parameters</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-blue-600 text-xl mt-1"></i>
                    <div>
                      <strong className="text-gray-900">Automated Alerts:</strong>
                      <span className="text-gray-600"> Instant notifications for any anomalies</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-blue-600 text-xl mt-1"></i>
                    <div>
                      <strong className="text-gray-900">Inventory Tracking:</strong>
                      <span className="text-gray-600"> Precise volume and quality monitoring</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-blue-600 text-xl mt-1"></i>
                    <div>
                      <strong className="text-gray-900">Remote Access:</strong>
                      <span className="text-gray-600"> Client portal for inventory visibility</span>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-blue-600">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">Secure Your Storage Today</h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Contact us to discuss your petroleum storage requirements and learn about our available capacity
            </p>
            <Link 
              to="/contact" 
              className="inline-block px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Started
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}